import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import type { Message } from '../types';

const PAGE_SIZE = 50;

// Admin parity (C2·P5). Group thread — reads messages by group_chat_id and sends
// through the canonical send_message RPC (p_message_type='group'). Mirrors the
// admin DM useThread; only the fetch key + the RPC payload differ.

export function useGroupThread(groupChatId: string | null) {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const businessId = business?.id;
  const currentUserId = user?.id;
  const mountedRef = useRef(true);

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const mapMsg = useCallback((m: Record<string, unknown>): Message => ({
    ...(m as object),
    isSent: m.sender_id === currentUserId,
    senderName: m.sender
      ? `${(m.sender as Record<string, string>).first_name} ${(m.sender as Record<string, string>).last_name}`
      : 'Unknown',
  }) as Message, [currentUserId]);

  const fetchMessages = useCallback(async () => {
    if (!groupChatId || !currentUserId) { setMessages([]); return; }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .select('*, sender:users!sender_id(first_name, last_name)')
        .eq('group_chat_id', groupChatId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .limit(PAGE_SIZE);
      if (err) throw err;
      if (!mountedRef.current) return;
      setMessages((data ?? []).map(mapMsg));
      setHasMore((data ?? []).length === PAGE_SIZE);
    } catch { /* silent */ }
    finally { if (mountedRef.current) setLoading(false); }
  }, [groupChatId, currentUserId, mapMsg]);

  useEffect(() => { void fetchMessages(); }, [fetchMessages]);

  // Realtime: append new group messages.
  useEffect(() => {
    if (!groupChatId) return;
    const ch = supabase
      .channel(`admin-group-thread-${groupChatId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages',
        filter: `group_chat_id=eq.${groupChatId}`,
      }, (payload) => {
        const msg = payload.new as Record<string, unknown>;
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          const optIdx = prev.findIndex(m => m.id.startsWith('opt-') && m.sender_id === msg.sender_id);
          const newMsg = mapMsg({ ...msg, sender: null });
          if (optIdx >= 0) {
            const next = [...prev];
            next[optIdx] = newMsg;
            return next;
          }
          return [...prev, newMsg];
        });
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [groupChatId, mapMsg]);

  const loadMore = useCallback(async () => {
    if (!groupChatId || messages.length === 0 || !hasMore) return;
    const oldest = messages[0];
    const { data } = await supabase
      .from('messages')
      .select('*, sender:users!sender_id(first_name, last_name)')
      .eq('group_chat_id', groupChatId)
      .is('deleted_at', null)
      .lt('created_at', oldest.created_at)
      .order('created_at', { ascending: true })
      .limit(PAGE_SIZE);
    if (!mountedRef.current) return;
    setMessages(prev => [...(data ?? []).map(mapMsg), ...prev]);
    setHasMore((data ?? []).length === PAGE_SIZE);
  }, [groupChatId, messages, hasMore, mapMsg]);

  const sendMessage = useCallback(async (content: string) => {
    if (!groupChatId || !businessId || !currentUserId || !content.trim()) return;
    setSending(true);

    const optId = `opt-${Date.now()}`;
    const optMsg: Message = {
      id: optId, conversation_id: null as unknown as string, sender_id: currentUserId,
      content: content.trim(), message_type: 'group', read_at: null,
      created_at: new Date().toISOString(), deleted_at: null,
      translations: null, original_language: null,
      reference_type: null, reference_id: null, reference_snapshot: null,
      sender: null, isSent: true, senderName: '',
    } as unknown as Message;
    setMessages(prev => [...prev, optMsg]);

    try {
      // Canonical send path: insert + group_chats preview update, atomic.
      const { data, error: err } = await supabase.rpc('send_message', {
        p_message_type: 'group',
        p_content: content.trim(),
        p_group_chat_id: groupChatId,
      });
      if (err) throw err;
      if (!data?.success) throw new Error(data?.error?.message ?? 'Failed to send');
      const realMsg = data.message as { id?: string } | null;

      const autoTranslate = (business as unknown as Record<string, unknown>)?.auto_translate_messages;
      if (realMsg?.id && autoTranslate !== false && content.trim().length >= 5) {
        supabase.functions.invoke('translate-message', {
          body: { message_id: realMsg.id, text: content.trim(), sender_id: currentUserId, group_chat_id: groupChatId },
        }).catch(() => { /* silent */ });
      }
    } catch {
      setMessages(prev => prev.filter(m => m.id !== optId));
      throw new Error('Failed to send');
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [groupChatId, businessId, currentUserId, business]);

  return { messages, loading, hasMore, loadMore, sendMessage, sending, refresh: fetchMessages };
}
